import {
  CellClassParams,
  GridOptions,
  HeaderClassParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueParserParams,
} from '@ag-grid-community/core';
import { inject, Injectable, Signal, WritableSignal } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject } from 'rxjs';

import { CustomCurvesQuery } from '@models/custom-curves/custom-curves.query';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { TableConstants } from '@shared/constants/table.constants';
import { CustomType } from '@shared/services/gql.service';
import { decimalAdd, decimalDivide, decimalMultiply, decimalRoundingToString } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { AgMonthCellComponent } from '../../components/ag-month-cell/ag-month-cell.component';
import { CustomCurvesNoRowsOverlayComponent } from '../components/custom-curves-no-rows-overlay/custom-curves-no-rows-overlay.component';
import {
  TotalDistributionComponentParams,
  TotalDistributionHeaderComponent,
} from '../components/total-distribution-header/total-distribution-header.component';
import { Distribution } from '../custom-drivers.component';

@Injectable({ providedIn: 'root' })
export class CustomCurvesUtils {
  private readonly customCurvesQuery = inject(CustomCurvesQuery);

  static calculateForecastDistributions(
    distributions: Distribution[],
    currentOpenMonth: string,
    type?: CustomType
  ): Distribution[] {
    const sameOrAfterOpenMonth = (distribution: Distribution) =>
      dayjs(distribution.distribution_month).isSameOrAfter(dayjs(currentOpenMonth), 'month');
    const distributionsForRemaining = distributions.filter(sameOrAfterOpenMonth);
    const plannedTotal = this.calculatePlannedTotal(distributions, type);
    const remainingTotal = this.calculatePlannedTotal(distributionsForRemaining, type);

    const calculateValue = (distribution: Distribution, total: Partial<Distribution>) => {
      return total.custom_amount
        ? decimalMultiply(
            decimalDivide(Number(distribution.custom_amount), total.custom_amount),
            100
          )
        : 0;
    };

    return distributions.map((distribution) => {
      const distribution_total =
        type === CustomType.CUSTOM_GROUP_PERCENTAGE
          ? distribution.custom_amount
          : calculateValue(distribution, plannedTotal);
      const distribution_remaining = sameOrAfterOpenMonth(distribution)
        ? calculateValue(distribution, remainingTotal)
        : 0;

      return {
        ...distribution,
        distribution_remaining,
        distribution_total,
      };
    });
  }

  static calculatePlannedTotal(
    distributions: Distribution[],
    type?: CustomType
  ): Partial<Distribution> {
    const isPercentages = type === CustomType.CUSTOM_GROUP_PERCENTAGE;
    const defaultTotal = distributions.some((distribution) => distribution.distribution_total != 0)
      ? 100
      : 0;
    const defaultRemainingTotal = distributions.some(
      (distribution) => distribution.distribution_remaining != 0
    )
      ? 100
      : 0;

    const data = distributions.reduce(
      (previous, current) => {
        const currentVal = previous['custom_amount'];
        const additionalVal = current['custom_amount'];

        if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
          previous['custom_amount'] = decimalAdd(currentVal, additionalVal);

          if (isPercentages) {
            previous['distribution_total'] = previous['custom_amount'];
          }
        }

        return previous;
      },
      {
        custom_amount: 0,
        distribution_remaining: defaultRemainingTotal,
        distribution_total: isPercentages ? 0 : defaultTotal,
      }
    );

    return data;
  }

  // eslint-disable-next-line max-lines-per-function
  getGridOptions({
    calculatedPinnedBottomData,
    currentOpenMonth$,
    editMode,
    onCellValueChanged,
    saveButtonTooltip,
    saving,
    selectedCurveDistributionLabel,
    timeline$,
  }: {
    calculatedPinnedBottomData: Signal<Partial<Distribution>>;
    currentOpenMonth$: BehaviorSubject<string>;
    editMode: Signal<boolean>;
    onCellValueChanged: () => void;
    saveButtonTooltip: WritableSignal<string>;
    saving: Signal<boolean>;
    selectedCurveDistributionLabel: Signal<string>;
    timeline$: BehaviorSubject<{
      endDate?: string | undefined;
      startDate?: string | undefined;
    }>;
  }): GridOptions {
    return {
      defaultColDef: {
        ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
        cellClass: (params: CellClassParams): string | string[] =>
          params.colDef.field === 'distribution_month' ? 'justify-start' : 'justify-end',
        cellClassRules: {
          '!bg-aux-error': (params) => {
            if (
              params.colDef.field === 'custom_amount' &&
              params.data.distribution_month === 'TOTAL'
            ) {
              const percentagesInvalid =
                this.customCurvesQuery.selectedCurve()?.custom_type ===
                  CustomType.CUSTOM_GROUP_PERCENTAGE &&
                !!calculatedPinnedBottomData().custom_amount &&
                (calculatedPinnedBottomData().custom_amount as number) !== 100;

              saveButtonTooltip.set(
                percentagesInvalid ? 'Total Percentage % must add to 100%.' : ''
              );

              return percentagesInvalid;
            }

            return false;
          },
        },
        cellStyle: (params) => {
          switch (params.colDef.field) {
            case 'distribution_remaining':
            case 'distribution_total':
              return editMode() || saving()
                ? { cursor: 'not-allowed', 'justify-items': 'end', opacity: 0.7 }
                : { 'justify-item': 'end' };
            default:
              return undefined;
          }
        },
        editable: (params) => editMode() && params.data.distribution_month !== 'TOTAL',
        headerClass: (params: HeaderClassParams): string | string[] =>
          (editMode() || saving()) && params.column?.getColId() === 'custom_amount'
            ? ['ag-header-edit-mode', 'ag-header-align-center']
            : ['ag-header-align-center'],
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          if (!params.editing) {
            switch (params.event.key) {
              case 'Backspace':
              case 'Delete':
                if (editMode()) {
                  onCellValueChanged();
                }
            }
          }

          return false;
        },
      },
      ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
      ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
      columnDefs: [
        {
          cellClass: 'custom-drivers__month-cell',
          cellRenderer: AgMonthCellComponent,
          cellRendererParams: {
            currentOpenMonth$,
            noMonthInTimelineTooltipMessage:
              'This month is no longer included in the trial timeline. Please adjust your Custom Curve or Trial Timeline to fix this issue.',
            timeline$,
          },
          editable: false,
          field: 'distribution_month',
          headerName: 'Month',
          minWidth: 175,
          sortable: false,
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value === 'TOTAL') {
              return 'TOTAL';
            }

            return params.value
              ? Utils.dateFormatter(params.value, { day: undefined, year: '2-digit' })
              : '';
          },
        },
        {
          aggFunc: 'sum',
          cellDataType: 'text',
          field: 'custom_amount',
          headerValueGetter: (): string => {
            return `Planned ${selectedCurveDistributionLabel()}`;
          },
          minWidth: 175,
          sortable: false,
          valueFormatter: this.valueFormatter.bind(this),
          valueParser(params: ValueParserParams): number | string {
            const num =
              params.newValue === params.oldValue
                ? params.oldValue
                : Utils.castStringToNumber(params.newValue).toString();

            return (Number.isNaN(num) ? params.oldValue : Math.abs(num)).toString();
          },
        },
        {
          aggFunc: 'sum',
          cellDataType: 'text',
          editable: false,
          field: 'distribution_total',
          headerComponent: TotalDistributionHeaderComponent,
          headerComponentParams: {
            collapsedByDefault: true,
            columnsToCollapse: ['distribution_remaining'],
            ignoreIfInvisible: [],
            localStorageKey: LocalStorageKey.CUSTOM_CURVES,
            template: `Forecast Distribution (Total)`,
          } as unknown as TotalDistributionComponentParams,
          headerName: `Forecast Distribution (Total)`,
          minWidth: 175,
          sortable: false,
          valueFormatter: this.valueFormatter.bind(this),
        },
        {
          aggFunc: 'sum',
          cellDataType: 'text',
          editable: false,
          field: 'distribution_remaining',
          headerName: `Forecast Distribution (Remaining)`,
          minWidth: 175,
          sortable: false,
          valueFormatter: this.valueFormatter.bind(this),
        },
      ],
      noRowsOverlayComponent: CustomCurvesNoRowsOverlayComponent,
      pinnedBottomRowData: [],
      processCellFromClipboard: (params) => {
        return Utils.castStringToNumber(params.value).toString();
      },
    } as GridOptions;
  }

  private valueFormatter(val: ValueFormatterParams): string {
    const newValue = val.value === '0' ? 0 : val.value;
    const selectedType = this.customCurvesQuery.selectedCurve()?.custom_type;

    const prefix =
      selectedType === CustomType.CUSTOM_GROUP_DOLLARS && val.colDef.field === 'custom_amount'
        ? '$'
        : '';
    const postfix =
      selectedType === CustomType.CUSTOM_GROUP_PERCENTAGE || val.colDef.field !== 'custom_amount'
        ? '%'
        : '';

    return newValue ? `${prefix}${decimalRoundingToString(newValue)}${postfix}` : Utils.zeroHyphen;
  }
}
